import React, { useEffect, useState } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore, storage } from '../firebase/firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import { SHOWROOM_COLLECTION, SHOWROOM_SETTINGS_ID } from '../components/showroom/showroomSettings';

const SHOWROOM_SETTINGS_REF = () => doc(firestore, SHOWROOM_COLLECTION, SHOWROOM_SETTINGS_ID);

const getStoragePathFromUrl = (imageUrl) => {
  try {
    const url = new URL(imageUrl);
    const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
    return path;
  } catch (error) {
    console.error('Invalid image URL:', error);
    return null;
  }
};

function ShowroomTopImageUpload() {
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const isFormValid = image !== null;

  useEffect(() => {
    const fetchCurrentImage = async () => {
      try {
        const snap = await getDoc(SHOWROOM_SETTINGS_REF());
        if (snap.exists() && snap.data().topImageUrl) {
          setCurrentImageUrl(snap.data().topImageUrl);
        }
      } catch (error) {
        console.error('ショールームトップ画像の取得に失敗:', error);
        setFetchError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentImage();
  }, []);

  useEffect(() => {
    if (!image) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(image);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImage(null);
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      alert('サポートされていないファイルタイプです。JPEG, PNG, GIF, WebPのみアップロードできます。');
      e.target.value = '';
      return;
    }

    if (file.size > maxSize) {
      alert('ファイルサイズが大きすぎます。10MB以下のファイルをアップロードしてください。');
      e.target.value = '';
      return;
    }

    setImage(file);
  };

  const deletePreviousImage = async (imageUrl) => {
    const imagePath = getStoragePathFromUrl(imageUrl);
    if (!imagePath) return;

    try {
      await deleteObject(ref(storage, imagePath));
    } catch (error) {
      console.error('既存の画像削除に失敗:', error);
    }
  };

  const saveTopImage = async () => {
    if (!isFormValid || isSaving) return;

    try {
      setIsSaving(true);

      const uniqueId = uuidv4();
      const fileExtension = image.name.split('.').pop();
      const uniqueFileName = `${uniqueId}.${fileExtension}`;
      const storageRef = ref(storage, `showroom/top/${uniqueFileName}`);

      await uploadBytes(storageRef, image);
      const imageUrl = await getDownloadURL(storageRef);

      await setDoc(
        SHOWROOM_SETTINGS_REF(),
        {
          topImageUrl: imageUrl,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      if (currentImageUrl) {
        await deletePreviousImage(currentImageUrl);
      }

      setCurrentImageUrl(imageUrl);
      setImage(null);
      setShowNotification(true);
    } catch (error) {
      console.error('ショールームトップ画像の保存に失敗:', error);
      alert(`エラー: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      width: '100%',
    },
    form: {
      width: '100%',
      maxWidth: '800px',
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
      textAlign: 'left',
      position: 'relative',
    },
    uploadTitle: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
    },
    currentImage: {
      marginBottom: '20px',
    },
    imagePreview: {
      width: '100%',
      maxHeight: '240px',
      objectFit: 'cover',
      borderRadius: '4px',
      border: '1px solid #ccc',
      display: 'block',
    },
    emptyText: {
      color: '#666',
      fontSize: '14px',
    },
    inputGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
    },
    inputField: {
      width: '100%',
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #ccc',
    },
    hint: {
      marginTop: '8px',
      color: '#666',
      fontSize: '12px',
    },
    submitButton: {
      padding: '10px 20px',
      backgroundColor: isFormValid && !isSaving ? '#110b41ff' : '#ccc',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: isFormValid && !isSaving ? 'pointer' : 'not-allowed',
      width: '100%',
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      display: isSaving ? 'flex' : 'none',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '8px',
      zIndex: 10,
    },
    loadingText: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    notification: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
      marginTop: '20px',
    },
    okButton: {
      padding: '10px',
      backgroundColor: '#110b41ff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      marginTop: '20px',
    },
    error: {
      color: 'red',
      marginBottom: '20px',
    },
  };

  const displayImageUrl = previewUrl || currentImageUrl;

  return (
    <div style={styles.container}>
      <form style={styles.form} onSubmit={(e) => e.preventDefault()}>
        <div style={styles.loadingOverlay}>
          <span style={styles.loadingText}>アップロード中...</span>
        </div>

        <div style={styles.uploadTitle}>
          <p>ショールームトップ画像</p>
        </div>

        <div style={styles.currentImage}>
          {isLoading ? (
            <p style={styles.emptyText}>読み込み中...</p>
          ) : fetchError ? (
            <p style={styles.error}>エラー: {fetchError}</p>
          ) : displayImageUrl ? (
            <img
              src={displayImageUrl}
              alt="ショールームトップ画像プレビュー"
              style={styles.imagePreview}
            />
          ) : (
            <p style={styles.emptyText}>未登録です。登録するまでフロントは既存のデフォルト画像を表示します。</p>
          )}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="showroomTopImage">画像ファイル:</label>
          <input
            id="showroomTopImage"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleImageUpload}
            style={styles.inputField}
            disabled={isSaving}
          />
          <p style={styles.hint}>JPEG / PNG / GIF / WebP、10MB以下。フロントの表示サイズは現状のままです。</p>
        </div>

        <button
          type="button"
          onClick={saveTopImage}
          style={styles.submitButton}
          disabled={!isFormValid || isSaving}
        >
          {isSaving ? 'アップロード中...' : isFormValid ? 'トップ画像を登録' : '画像を選択してください'}
        </button>
      </form>

      {showNotification && (
        <div style={styles.notification}>
          <p>トップ画像を登録しました。</p>
          <button onClick={() => setShowNotification(false)} style={styles.okButton}>OK</button>
        </div>
      )}
    </div>
  );
}

export default ShowroomTopImageUpload;
